import { useCallback, useMemo } from 'react';
import type { ReferralWorkflowState, UseReferralPersistenceReturn } from '../types/referral';
import { REFERRAL_STORAGE } from '@/constants/storage';
import { getLogger } from '@/services/shared/LoggerFactory';

/**
 * Custom hook for managing referral workflow data persistence in localStorage
 * Follows BiteWise patterns for error handling and storage management
 */
export const useReferralPersistence = (): UseReferralPersistenceReturn => {
  const logger = getLogger();
  // Check if localStorage is available (handles SSR and private browsing)
  const isStorageAvailable = useMemo(() => {
    try {
      if (typeof window === 'undefined') return false;
      const test = '__storage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  }, []);

  // Save workflow data to localStorage
  const saveToStorage = useCallback((data: Partial<ReferralWorkflowState>) => {
    if (!isStorageAvailable) return;

    try {
      const existingData = loadFromStorage();
      const mergedData = { ...existingData, ...data };

      // Remove sensitive data that shouldn't be persisted
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { isLoading, isSubmitting, submissionResult, ...persistableData } = mergedData;

      localStorage.setItem(REFERRAL_STORAGE.WORKFLOW, JSON.stringify(persistableData));
    } catch (error) {
      logger.warn('Failed to save referral data to localStorage:', error);
    }
  }, [isStorageAvailable]);

  // Load workflow data from localStorage
  const loadFromStorage = useCallback((): Partial<ReferralWorkflowState> | null => {
    if (!isStorageAvailable) return null;

    try {
      const stored = localStorage.getItem(REFERRAL_STORAGE.WORKFLOW);
      if (!stored) return null;

      const parsed = JSON.parse(stored);

      // Validate the structure of loaded data
      if (typeof parsed !== 'object' || parsed === null) {
        return null;
      }

      // Ensure we have valid step number
      if (parsed.currentStep && ![1, 2, 3].includes(parsed.currentStep)) {
        parsed.currentStep = 1;
      }

      return parsed;
    } catch (error) {
      logger.warn('Failed to load referral data from localStorage:', error);
      // Clear corrupted data
      clearStorage();
      return null;
    }
  }, [isStorageAvailable]);

  // Clear all workflow data from localStorage
  const clearStorage = useCallback(() => {
    if (!isStorageAvailable) return;

    try {
      localStorage.removeItem(REFERRAL_STORAGE.WORKFLOW);
    } catch (error) {
      logger.warn('Failed to clear referral data from localStorage:', error);
    }
  }, [isStorageAvailable]);

  return {
    saveToStorage,
    loadFromStorage,
    clearStorage,
    isStorageAvailable,
  };
};